import { Options, Series, SeriesOptionsType, TooltipOptions } from 'highcharts';
import { isEmpty } from 'lodash';
import { Dispatch, SetStateAction, useRef } from 'react';
import { GrowthTypes } from '../../components/comparison-growth-selector/Types';
import {
  BaseChartResp,
  BaseChartValue,
  BubbleChartOptionsParam,
  BubbleChartResp,
  isMarketRetailerType,
  RetailerType,
  SaleType,
} from '@models/OpportunityReview';
import zoomButtonRender from '@pages/opportunity-review/ZoomButton';
import { formatKpi, Kpi, ValueType } from '@utils/Formatters';
import { chartOption } from '../utilities/CommonOptions';
import * as styles from './OpportunityReviewBubbleChart.module.scss';
import { getDataByRetailerType } from './OpportunityReviewBubbleChartUtil';
import { clear, drawAllAnnotations } from './Quadrant';
import { leftIconLabel, TooltipPosition } from './Tooltip';
import { FactRevenue } from '@utils/pageConfig/Types';
import { constructCurrencyLabel, formatLabelIntoRevenuePriceTitle } from '@utils/currencyUtils/CurrencyUtils';

enum YAxisLabel {
  TITLE = 'Growth vs Market',
  TEXT = `For readability, axis fixed to highest growth/decline seen across MAT period. Extreme growth differences
  outside this range in individual periods not plotted on chart, can be reviewed in table view.`,
}

const getYourShareGrowth = (yourShare: number | undefined, fairShareNumber: number | undefined) => {
  if (yourShare && fairShareNumber) {
    return yourShare - fairShareNumber;
  }
  return 0;
};

const buildMarketRetailerSeriesData = (
  data: BaseChartResp[],
  selectedPeriod: string,
  retailerType: RetailerType,
  isDataChanged: boolean,
  setIsDataChanged: Dispatch<SetStateAction<boolean>>,
  selectedBrandBubble: string,
): SeriesOptionsType[] => {
  const normalColor = '#81d7d3';
  const selectedColor = '#879bcc';

  const marketRetailerData = getBubbleChartDataForSpecificPeriod(data, selectedPeriod);
  const hasData = marketRetailerData && !isEmpty(marketRetailerData.values);

  // Return a default value if there's no data, this is required as returning an empty list will
  // cause the bubble chart to render a blank chart
  if (!hasData) {
    return [
      {
        type: 'bubble',
        data: [],
      },
    ];
  }

  /*
      visible: true -> helps to reset the state of the bubble chart
  
      We only want this to happen when the data, selected period, or retailer type selection changes
      If we do not check isDataChanged, the bubble chart will reset each time we click on something in the UI, eg. opening filter tray
    */
  const series = marketRetailerData.values.map((entry: BaseChartValue) => ({
    name: entry.title,
    color: selectedBrandBubble === entry.title ? selectedColor : normalColor,
    type: 'bubble',
    states: buildCommonChartStates(),
    data: buildSeriesData(entry, retailerType),
    ...(isDataChanged && { visible: true }),
  }));
  setIsDataChanged(false);
  return series as SeriesOptionsType[];
};

const buildSeriesData = (entry: BaseChartValue, retailerType: RetailerType) => {
  return [
    {
      name: entry.title,
      x: entry.yourShare, // As mentioned by Liam, yourShare should be displayed as x coordinate
      y: entry.ycoordinate,
      z: isMarketRetailerType(retailerType) ? entry.marketValue : entry.yourValue,
    },
  ];
};

const buildCommonChartStates = () => {
  return {
    hover: {
      halo: null,
    },
  };
};

const buildToolTipItemBySaleType = (value: number | undefined, currency: string | undefined, saleType: SaleType) => {
  return saleType === SaleType.VALUE
    ? formatKpi(Kpi.revenue, value, {
        currencySymbol: currency,
      })
    : formatKpi(Kpi.salesVolume, value);
};

const buildTooltip = (
  data: BubbleChartResp,
  retailerType: RetailerType,
  selectedPeriod: string,
  selectedGrowthType: GrowthTypes,
  factRevenue: FactRevenue,
  saleType: SaleType,
  volume: string,
): TooltipOptions => {
  const hasData = data.content.length > 0;
  const marketContent = hasData ? data.content[0].market : [];
  const retailerContent = hasData ? data.content[0].retailer : [];

  const values = isMarketRetailerType(retailerType)
    ? getBubbleChartDataForSpecificPeriod(marketContent, selectedPeriod)?.values
    : getBubbleChartDataForSpecificPeriod(retailerContent, selectedPeriod)?.values;

  const fairShareNumber = isMarketRetailerType(retailerType)
    ? getBubbleChartDataForSpecificPeriod(marketContent, selectedPeriod)?.fairShareNumber
    : getBubbleChartDataForSpecificPeriod(retailerContent, selectedPeriod)?.fairShareNumber;

  let revenueTitle: string = '';
  const currencyLabel = constructCurrencyLabel(factRevenue);

  switch (saleType) {
    case SaleType.VALUE:
      revenueTitle = isMarketRetailerType(retailerType) ? 'Market Revenue' : 'Retailer Revenue';
      break;
    case SaleType.VOLUME:
      revenueTitle = isMarketRetailerType(retailerType) ? `Market  ${volume}` : `Retailer ${volume}`;
      break;
  }

  return {
    shape: 'square',
    useHTML: true,
    backgroundColor: 'transparent',
    shared: true,
    borderWidth: 0,
    borderRadius: 3,
    shadow: false,
    formatter: function () {
      const point = this.point;

      if (!point) {
        return;
      }

      const { name } = point as typeof point & {
        name: string;
      };

      const value = values?.find((val) => val.title === name);

      const yourShareGrowth = getYourShareGrowth(value?.yourShare, fairShareNumber);

      const revenue = isMarketRetailerType(retailerType) ? value?.marketValue : value?.yourValue;

      return `
        <div class="${styles.ChartTooltip} z-index-default">
          <div class="${styles.ChartTooltip__Heading}">${name}</div>
          <div class="${styles.ChartTooltip__Row}">
            <div class="${styles.ChartTooltip__Label}">Your Share</div>
            <div class="${styles.ChartTooltip__Value}">${formatKpi(Kpi.revenueShare, value?.yourShare, {
        isDecimalPercent: true,
      })} (${formatKpi(Kpi.revenueShare, yourShareGrowth, {
        as: ValueType.ABSOLUTE_CHANGE,
      })} vs fair share)</div>
          </div>
          <div class="${styles.ChartTooltip__Row}">
            <div class="${styles.ChartTooltip__Label}">Growth</div>
            <div class="${styles.ChartTooltip__Value}">${
        selectedGrowthType === GrowthTypes.percentage
          ? formatKpi(Kpi.revenueShare, value?.yourGrowthPercentage, { isDecimalPercent: true })
          : formatKpi(Kpi.salesVolume, value?.yourGrowthAbsolute)
      } ( ${formatKpi(Kpi.revenueShare, value?.ycoordinate, {
        as: ValueType.ABSOLUTE_CHANGE,
      })} vs Market)</div>
          </div>
          <div class="${styles.ChartTooltip__Row}">
            <div class="${styles.ChartTooltip__Label}">${formatLabelIntoRevenuePriceTitle(
        revenueTitle,
        currencyLabel,
      )}</div>
            <div class="${styles.ChartTooltip__Value}">${buildToolTipItemBySaleType(
        revenue,
        factRevenue.currencySymbol,
        saleType,
      )}
            </div>
          </div>
          <div class="${styles.ChartTooltip__Border}"></div>
          <div class="${styles.ChartTooltip__Row}">
            <div class="${styles.ChartTooltip__Label}">Growth Opportunity</div>
            <div class="${styles.ChartTooltip__Value}">${buildToolTipItemBySaleType(
        value?.growthOpportunity,
        factRevenue.currencySymbol,
        saleType,
      )}</div>
          </div>
          <div class="${styles.ChartTooltip__Row}">
            <div class="${styles.ChartTooltip__Label}">Share Opportunity</div>
            <div class="${styles.ChartTooltip__Value}">${buildToolTipItemBySaleType(
        value?.shareOpportunity,
        factRevenue.currencySymbol,
        saleType,
      )}</div>
          </div>
        </div>
      `;
    },
  };
};

const getBubbleChartDataForSpecificPeriod = (chartData: BaseChartResp[], selectedPeriod: string) => {
  return chartData.find((data) => data.period === formatToCompactWeekRange(selectedPeriod));
};

function formatToCompactWeekRange(period: string): string {
  const fullPattern = /^WEEK \d{1,2} \d{4} - WEEK \d{1,2} \d{4}$/;

  if (fullPattern.test(period)) {
    const weekPattern = /WEEK (\d{1,2}) (\d{4})/g;
    const formatWeek = (week: string, year: string): string => {
      const formattedWeek = week.padStart(2, '0');
      const formattedYear = year.slice(-2);

      return `W${formattedWeek}-${formattedYear}`;
    };

    return period.replace(weekPattern, (_, week, year) => formatWeek(week, year));
  }

  return period;
}

export const useGetChartOptions = (bubbleChartOptionsParam: BubbleChartOptionsParam): Options => {
  // A set of references to keep track of the label dynamically added to Highcharts.
  const topLeftLabel = useRef<Highcharts.SVGElement | null>(null);
  const topRightLabel = useRef<Highcharts.SVGElement | null>(null);
  const bottomLeftLabel = useRef<Highcharts.SVGElement | null>(null);
  const bottomRightLabel = useRef<Highcharts.SVGElement | null>(null);

  const {
    data,
    retailerType,
    selectedPeriod,
    axesRange,
    isExportModalOpen,
    selectedBrandBubble,
    setSelectedBrandBubble,
    volume,
  } = bubbleChartOptionsParam;

  const dataArray: BaseChartResp[] = getDataByRetailerType(data.content, retailerType);
  const bubbleChartData = getBubbleChartDataForSpecificPeriod(dataArray, selectedPeriod);
  const hasData = !isEmpty(bubbleChartData?.values);

  function redrawBubbleChartLabels(this: Highcharts.Chart) {
    const labels = {
      topLeftLabel,
      topRightLabel,
      bottomLeftLabel,
      bottomRightLabel,
    };
    clear.apply(this, [labels]);
    drawAllAnnotations.apply(this, [labels, isExportModalOpen]);
  }

  return {
    chart: {
      type: 'bubble',
      plotBorderWidth: 1,
      spacing: [60, 10, 100, 10],
      height: 800,
      events: {
        load: redrawBubbleChartLabels,
        redraw: redrawBubbleChartLabels,
        render() {
          //Hide the zoom button while exporting to PPT/Image
          if (!isExportModalOpen) {
            zoomButtonRender(this, axesRange);
          }
        },
      },
      zoomType: 'xy',
    },
    title: chartOption.title,
    credits: chartOption.credits,
    legend: {
      // Only enable the legend if bubble chart has data
      enabled: hasData,
      y: 100,
      verticalAlign: 'bottom',
    },
    xAxis: {
      gridLineWidth: 1,
      title: {
        text: 'Share',
        y: 20,
      },
      labels: {
        format: '{value}%',
      },
      plotLines: [
        {
          color: 'black',
          dashStyle: 'Dash',
          width: 1.25,
          value: bubbleChartData?.fairShareNumber,
        },
      ],
      ...(axesRange && axesRange.xAxisRange),
    },
    yAxis: {
      type: 'logarithmic',
      startOnTick: false,
      endOnTick: false,
      title: {
        text: `<div data-testid="y-axis-label">${leftIconLabel(
          YAxisLabel.TITLE,
          YAxisLabel.TEXT,
          TooltipPosition.TOP_LEFT,
          isExportModalOpen,
        )}</div>`,
        align: 'high',
        textAlign: 'center',
        rotation: 0,
        useHTML: true,
        offset: 85,
        y: -10,
      },
      labels: {
        format: '{value}%',
      },
      maxPadding: 0.2,
      plotLines: [
        {
          color: 'black',
          dashStyle: 'Dash',
          width: 1.25,
          value: 0,
        },
      ],
      ...(axesRange && axesRange.yAxisRange),
    },
    tooltip: buildTooltip(
      data,
      retailerType,
      selectedPeriod,
      bubbleChartOptionsParam.selectedGrowthType,
      bubbleChartOptionsParam.factRevenue,
      bubbleChartOptionsParam.saleType,
      volume,
    ),
    plotOptions: {
      bubble: {
        dataLabels: {
          useHTML: true,
          style: {
            color: 'text-heading',
            textOutline: '',
            fontWeight: 'normal',
          },
        },
        events: {
          click: function () {
            const bubble: Series = this;
            if (selectedBrandBubble !== bubble.name) {
              setSelectedBrandBubble(bubble.name);
            } else {
              setSelectedBrandBubble('');
            }
          },
        },
      },
      series: {
        dataLabels: {
          enabled: true,
          format: '{point.name}',
        },
      },
    },
    series: buildMarketRetailerSeriesData(
      getDataByRetailerType(data.content, retailerType),
      selectedPeriod,
      retailerType,
      bubbleChartOptionsParam.isDataChanged,
      bubbleChartOptionsParam.setIsDataChanged,
      selectedBrandBubble,
    ),
  };
};
